import React, { useState, useEffect, useMemo } from 'react';
import { List } from 'semantic-ui-react';
import Header from 'components/Header';
import Message from 'components/Message';
import API from 'utils/api';
import MicroBillProductMenu from './BillProductMenu';
import CancelGoButtons from 'components/CancelGoButtons';

function buildInitialCart(editOrder) {
  if (!editOrder) return [];
  return [{
    product: {
      id: editOrder.product_id,
      name: editOrder.product_name,
      out_of_stock: false
    },
    quantity: editOrder.quantity
  }];
}

function findProductInMenu(menuData, productId) {
  for (let i = 0; i < menuData.length; i += 1) {
    const products = menuData[i][1];
    const found = products.find((p) => p.id === productId);
    if (found) return found;
  }
  return null;
}

export default function AddOrdersMenu({ cancelFn, billId, getBill, editOrder }) {
  const [menu, setMenu] = useState([]);
  const [orders, setOrders] = useState(() => buildInitialCart(editOrder));
  const [filteredMenu, setFilteredMenu] = useState(menu);

  const productForEdit = useMemo(() => {
    if (!editOrder || menu.length === 0) return null;
    return findProductInMenu(menu, editOrder.product_id) || {
      id: editOrder.product_id,
      name: editOrder.product_name,
      out_of_stock: false,
    };
  }, [editOrder, menu]);

  const addOrders = () => {
    const params = {
      orders: orders.map(order => ({
        product_id: order.product.id,
        quantity: order.quantity
      }))
    };

    const request = editOrder
      ? API.updateBillOrders(params, billId)
      : API.addOrders(params, billId);

    request
      .then(() => {
        cancelFn();
        getBill();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const addOrUpdateOrder = (product, quantity) => {
    const isOrderInList = orders.some(order => order.product.id === product.id);

    if (isOrderInList) {
      const updatedOrders = orders.map(order => {
        if (order.product.id === product.id) {
          return { ...order, quantity: quantity };
        }
        return order;
      }).filter(order => order.quantity !== 0);
      setOrders(updatedOrders);
    } else if (quantity > 0) {
      setOrders([...orders, { product, quantity }]);
    }
  };

  useEffect(() => {
    API.getProducts()
      .then((response) => {
        setMenu(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    if (!editOrder || menu.length === 0) return;
    const full = findProductInMenu(menu, editOrder.product_id);
    if (!full) return;
    setOrders((prev) =>
      prev.map((o) => (o.product.id === full.id ? { ...o, product: full } : o))
    );
  }, [editOrder, menu]);

  useEffect(() => {
    setFilteredMenu(menu);
  }, [menu]);

  const headerText = editOrder ? 'Editar artículo en la cuenta' : 'Agregar a la cuenta del menú';
  const goText = editOrder ? 'guardar cambios' : 'agregar ordenes';

  const renderAddMenu = () => {
    if (filteredMenu.length === 0) {
      return (
        <Message>
          <p>Aún no has agregado productos a tu menú.
            Dale click <a href="/menu/products/new">aquí</a> para agregarlos.
          </p>
        </Message>
      );
    }
    return (
      <div style={{
        border: 'none',
        borderTop: '1px solid #f0f0f0',
        margin: '8px 0 16px 0'
      }}>
        <List divided={false}>
          {filteredMenu.map((categoryProducts) => (
            <List.Item
              key={categoryProducts[0]}
            >
              <List.Content style={{ margin: '1em 0 0.5em 0', fontSize: '1.4em', paddingLeft: '0' }}>
                <List.Header style={{ padding: '6px 0' }}>
                  {categoryProducts[0]}
                </List.Header>
                <List.List>
                  {categoryProducts[1].map((product) => (
                    <MicroBillProductMenu
                      key={product.id}
                      product={product}
                      billItems={orders}
                      addOrUpdateBillItem={addOrUpdateOrder}
                    />
                  ))}
                </List.List>
              </List.Content>
            </List.Item>
          ))}
        </List>
      </div>
    );
  };

  const renderEditSingle = () => {
    if (menu.length === 0) {
      return (
        <Message>
          Cargando…
        </Message>
      );
    }
    return (
      <div style={{
        border: 'none',
        borderTop: '1px solid #f0f0f0',
        margin: '8px 0 16px 0'
      }}>
        <List divided={false}>
          <MicroBillProductMenu
            product={productForEdit}
            billItems={orders}
            addOrUpdateBillItem={addOrUpdateOrder}
          />
        </List>
      </div>
    );
  };

  return (
    <div>
      <Header text={headerText} />
      <div>
        {editOrder ? renderEditSingle() : renderAddMenu()}

        <CancelGoButtons
          cancelFn={cancelFn}
          goText={goText}
          goEnabled={orders.length > 0}
          goFn={addOrders}
        />
      </div>
    </div>
  );
}
